import { FC } from 'react'
import {
    VStack,
    Container,
    Heading,
    Text,
    SimpleGrid,
    Box,
    Image,
    useColorModeValue,
    HStack,
    Badge,
    Icon,
    Flex,
} from '@chakra-ui/react'
import { Element } from 'react-scroll'
import Fade from 'react-reveal/Fade'
import { FaArrowRight, FaPlane, FaShip } from 'react-icons/fa'

const skincareImages = [
    '/PHOTO-2026-03-23-05-55-06.jpg',
    '/PHOTO-2026-03-23-05-55-06 2.jpg',
    '/PHOTO-2026-03-23-05-55-06 3.jpg',
]

const vehicleShipments = [
    {
        before: { src: '/ml350_white_US.JPG', location: '🇺🇸 USA' },
        after: { src: '/ml350_white_NG.JPG', location: '🇳🇬 Nigeria' },
        label: 'ML350 White',
    },
    {
        before: { src: '/ml350_gray_US.JPG', location: '🇺🇸 USA' },
        after: { src: '/ml350_gray_NG.JPG', location: '🇳🇬 Nigeria' },
        label: 'ML350 Gray',
    },
]

const Shipments: FC = () => {
    const cardBg = useColorModeValue('white', 'gray.800')
    const borderColor = useColorModeValue('gray.200', 'gray.700')
    const textColor = useColorModeValue('gray.600', 'gray.300')
    const labelColor = useColorModeValue('gray.700', 'gray.200')

    return (
        <Element name='shipments'>
            <Container maxW={'6xl'} p={6.75} mt={20} mb={[10, 20]}>
                <VStack spacing={8}>
                    <Fade>
                        <Heading as='h2'>Recent Shipments</Heading>
                    </Fade>
                    <Fade bottom>
                        <Box
                            bg={cardBg}
                            p={6}
                            borderRadius='lg'
                            border='1px'
                            borderColor={borderColor}
                            maxW='3xl'
                            textAlign='center'
                        >
                            <Text
                                fontSize='lg'
                                color={textColor}
                                lineHeight='tall'
                            >
                                <Text as='span' fontWeight='bold' color='cyan.500'>
                                    $700K Container Consignment
                                </Text>{' '}
                                — Successfully coordinated and executed a high-value international shipment of{' '}
                                <Text as='span' fontWeight='semibold'>
                                    high-demand Anua and Tiam skincare products
                                </Text>{' '}
                                for the West African market, shipped from{' '}
                                <Text as='span' fontWeight='semibold'>
                                    Seoul, South Korea
                                </Text>{' '}
                                to{' '}
                                <Text as='span' fontWeight='semibold'>
                                    Lagos, Nigeria
                                </Text>{' '}
                                via{' '}
                                <Text as='span' fontWeight='semibold'>
                                    CMA CGM
                                </Text>
                                . This consignment involved end-to-end logistics management including vendor coordination, customs documentation, freight forwarding, and real-time tracking through our proprietary TrackForce platform.
                            </Text>
                        </Box>
                    </Fade>
                    <SimpleGrid columns={[1, 2, 3]} spacing={6} w='full'>
                        {skincareImages.map((src, index) => (
                            <Fade bottom delay={index * 100} key={index}>
                                <Box
                                    borderRadius='lg'
                                    overflow='hidden'
                                    border='1px'
                                    borderColor={borderColor}
                                    boxShadow='md'
                                    transition='transform 0.3s ease'
                                    _hover={{ transform: 'scale(1.02)' }}
                                >
                                    <Image
                                        src={src}
                                        alt={`Skincare shipment photo ${index + 1}`}
                                        w='full'
                                        h='250px'
                                        objectFit='cover'
                                    />
                                </Box>
                            </Fade>
                        ))}
                    </SimpleGrid>

                    {/* Vehicle Shipment Section */}
                    <Box mt={16} w='full'>
                        <Fade bottom>
                            <Box
                                bg={cardBg}
                                p={6}
                                borderRadius='lg'
                                border='1px'
                                borderColor={borderColor}
                                maxW='3xl'
                                mx='auto'
                                textAlign='center'
                                mb={8}
                            >
                                <Text
                                    fontSize='lg'
                                    color={textColor}
                                    lineHeight='tall'
                                >
                                    <Text as='span' fontWeight='bold' color='cyan.500'>
                                        Mercedes-Benz ML350 Vehicle Export
                                    </Text>{' '}
                                    — Coordinated the international shipment of{' '}
                                    <Text as='span' fontWeight='semibold'>
                                        Mercedes-Benz ML350 vehicles
                                    </Text>{' '}
                                    from the{' '}
                                    <Text as='span' fontWeight='semibold'>
                                        United States
                                    </Text>{' '}
                                    to{' '}
                                    <Text as='span' fontWeight='semibold'>
                                        Lagos, Nigeria
                                    </Text>{' '}
                                    on behalf of dealership clients. Shipped via{' '}
                                    <Text as='span' fontWeight='semibold'>
                                        Sallaum Lines
                                    </Text>
                                    , the vehicles were delivered{' '}
                                    <Text as='span' fontWeight='semibold' color='green.500'>
                                        ahead of schedule
                                    </Text>
                                    , saving the client approximately{' '}
                                    <Text as='span' fontWeight='bold' color='green.500'>
                                        ~18% in costs
                                    </Text>{' '}
                                    through Okoye Exports' optimized logistics and vendor partnerships.
                                </Text>
                            </Box>
                        </Fade>
                        <VStack spacing={8} w='full'>
                            {vehicleShipments.map((shipment, index) => (
                                <Fade bottom delay={index * 150} key={index}>
                                    <Box w='full'>
                                        <Text
                                            fontSize='lg'
                                            fontWeight='bold'
                                            textAlign='center'
                                            mb={4}
                                            color={labelColor}
                                        >
                                            {shipment.label}
                                        </Text>
                                        <Flex
                                            direction={['column', 'row']}
                                            align='center'
                                            justify='center'
                                            gap={[4, 6]}
                                        >
                                            {/* Before Image - USA */}
                                            <Box
                                                borderRadius='lg'
                                                overflow='hidden'
                                                border='2px'
                                                borderColor='blue.400'
                                                boxShadow='lg'
                                                transition='transform 0.3s ease'
                                                _hover={{ transform: 'scale(1.02)' }}
                                                position='relative'
                                                w={['280px', '280px', '300px']}
                                                h='auto'
                                            >
                                                <Badge
                                                    position='absolute'
                                                    top={2}
                                                    left={2}
                                                    colorScheme='blue'
                                                    fontSize='sm'
                                                    px={2}
                                                    py={1}
                                                    borderRadius='md'
                                                    zIndex={1}
                                                >
                                                    BEFORE
                                                </Badge>
                                                <Image
                                                    src={shipment.before.src}
                                                    alt={`${shipment.label} in USA`}
                                                    w='100%'
                                                    h='220px'
                                                    objectFit='cover'
                                                />
                                                <Box
                                                    bg='blue.500'
                                                    p={2}
                                                >
                                                    <Text
                                                        fontSize='md'
                                                        fontWeight='bold'
                                                        color='white'
                                                        textAlign='center'
                                                    >
                                                        {shipment.before.location}
                                                    </Text>
                                                </Box>
                                            </Box>

                                            {/* Arrow/Ship Icon */}
                                            <VStack spacing={1} px={4}>
                                                <Icon
                                                    as={FaShip}
                                                    boxSize={8}
                                                    color='cyan.500'
                                                />
                                                <HStack spacing={1}>
                                                    <Box w='30px' h='2px' bg='cyan.500' />
                                                    <Icon
                                                        as={FaArrowRight}
                                                        boxSize={6}
                                                        color='cyan.500'
                                                    />
                                                </HStack>
                                                <Text
                                                    fontSize='xs'
                                                    fontWeight='semibold'
                                                    color='cyan.500'
                                                >
                                                    SALLAUM
                                                </Text>
                                            </VStack>

                                            {/* After Image - Nigeria */}
                                            <Box
                                                borderRadius='lg'
                                                overflow='hidden'
                                                border='2px'
                                                borderColor='green.400'
                                                boxShadow='lg'
                                                transition='transform 0.3s ease'
                                                _hover={{ transform: 'scale(1.02)' }}
                                                position='relative'
                                                w={['280px', '280px', '300px']}
                                                h='auto'
                                            >
                                                <Badge
                                                    position='absolute'
                                                    top={2}
                                                    left={2}
                                                    colorScheme='green'
                                                    fontSize='sm'
                                                    px={2}
                                                    py={1}
                                                    borderRadius='md'
                                                    zIndex={1}
                                                >
                                                    AFTER
                                                </Badge>
                                                <Image
                                                    src={shipment.after.src}
                                                    alt={`${shipment.label} in Nigeria`}
                                                    w='100%'
                                                    h='220px'
                                                    objectFit='cover'
                                                />
                                                <Box
                                                    bg='green.500'
                                                    p={2}
                                                >
                                                    <Text
                                                        fontSize='md'
                                                        fontWeight='bold'
                                                        color='white'
                                                        textAlign='center'
                                                    >
                                                        {shipment.after.location}
                                                    </Text>
                                                </Box>
                                            </Box>
                                        </Flex>
                                    </Box>
                                </Fade>
                            ))}
                        </VStack>
                    </Box>
                </VStack>
            </Container>
        </Element>
    )
}

export default Shipments
